import React from 'react';
import { SFCircle } from 'sf-symbols-lib/monochrome/SFCircle';
import { SFFigureRun } from 'sf-symbols-lib/monochrome/SFFigureRun';
import { SFWaveformPathEcg } from 'sf-symbols-lib/monochrome/SFWaveformPathEcg';
import './TrainingFocusCard.css';

const focusCopy = {
  1: { title: 'Recovery First', description: 'Your plan will skip endurance work and emphasize recovery sessions.', tone: 'recovery' },
  2: { title: 'Recovery Leaning', description: 'Your plan keeps endurance light while building a stronger recovery base.', tone: 'recovery' },
  3: { title: 'Balanced', description: 'Your plan splits work between endurance training and recovery equally.', tone: 'ayro' },
  4: { title: 'Endurance Leaning', description: 'Your plan adds more endurance training sessions while preserving recovery work.', tone: 'endurance' },
  5: { title: 'Endurance First', description: 'Your plan pushes endurance as far as possible while keeping a couple recovery sessions.', tone: 'endurance' },
};

function TrainingFocusCard({ initialFocus = 4 }) {
  const [focus, setFocus] = React.useState(clampFocus(initialFocus));
  const copy = focusCopy[focus];

  return (
    <article className={`ayro-focusCard ayro-focusCard--${copy.tone}`} aria-label="Training balance slider">
      <div className="ayro-focusCard__top">
        <div>
          <p>TRAINING BALANCE</p>
          <h3>{copy.title}</h3>
        </div>
        <div className="ayro-focusCard__badge" aria-hidden="true">
          {copy.tone === 'recovery' ? <RecoveryIcon /> : copy.tone === 'endurance' ? <EnduranceIcon /> : <SliderIcon />}
        </div>
      </div>
      <TrainingFocusSlider value={focus} onChange={setFocus} />
      <div className="ayro-focusBenefits">
        <FocusBenefitCard tone="recovery" title="RECOVERY" text="Improve your HRV and nervous system to recover faster and become more stress resilient." icon={<RecoveryIcon />} />
        <FocusBenefitCard tone="endurance" title="ENDURANCE" text="Increase your CO2 tolerance to gain better breath control and improve stamina and endurance." icon={<EnduranceIcon />} />
      </div>
      <p className="ayro-focusCard__description">{copy.description}</p>
    </article>
  );
}

function TrainingFocusSlider({ value, onChange }) {
  const knobPercent = ((value - 1) / 4) * 100;
  const knobShift = value === 1 ? '0%' : value === 5 ? '-100%' : '-50%';

  return (
    <div className="ayro-focusSlider">
      <div className="ayro-focusSlider__trackWrap">
        <div className="ayro-focusSlider__track" />
        {[2, 3, 4].map((notch) => (
          <span key={notch} className={`ayro-focusSlider__notch ${notch === value ? 'is-active' : ''}`} style={{ left: `${((notch - 1) / 4) * 100}%` }} />
        ))}
        <span className="ayro-focusSlider__knob" style={{ left: `${knobPercent}%`, '--knob-shift': knobShift }} />
        <input aria-label="Training focus" type="range" min="1" max="5" step="1" value={value} onChange={(event) => onChange(clampFocus(Number(event.target.value)))} />
      </div>
      <div className="ayro-focusSlider__labels">
        <span>RECOVERY</span>
        <span>ENDURANCE</span>
      </div>
    </div>
  );
}

function FocusBenefitCard({ tone, title, text, icon }) {
  return (
    <div className={`ayro-focusBenefit ayro-focusBenefit--${tone}`}>
      <span className="ayro-focusBenefit__icon" aria-hidden="true">{icon}</span>
      <div>
        <strong>{title}</strong>
        <p>{text}</p>
      </div>
    </div>
  );
}

function clampFocus(value) {
  return Math.min(5, Math.max(1, Math.round(value || 4)));
}

function RecoveryIcon() {
  return <SFWaveformPathEcg />;
}

function EnduranceIcon() {
  return <SFFigureRun />;
}

function SliderIcon() {
  return <SFCircle />;
}

export default TrainingFocusCard;
